import React, { useEffect, useId, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LinkIcon from "@mui/icons-material/Link";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import { BannerContentTypeEnum, BannerInput } from "../../../graphql/types";
import { ConfirmDialog } from "../dashboard/ConfirmDialog";
import { ModalShell } from "../dashboard/ModalShell";

interface BannerPickerProps {
  formikVal: BannerInput[];
  formikSetFieldValue: (banners: BannerInput[]) => void;
  pickerLabel: string;
  disabled?: boolean;
}

type BannerMode = "image" | "video";

const getYouTubeVideoId = (url: string): string | null => {
  const match = url.match(
    /^.*(?:youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/,
  );

  return match?.[1]?.length === 11 ? match[1] : null;
};

const compressImage = (
  file: File,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.8,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const image = new Image();
      image.onload = () => {
        let { width, height } = image;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");

        if (!context) {
          resolve(String(event.target?.result || ""));
          return;
        }

        context.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      image.onerror = () => reject(new Error("Failed to load image"));
      image.src = String(event.target?.result || "");
    };
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });

const BannerPicker: React.FC<BannerPickerProps> = ({
  formikVal = [],
  formikSetFieldValue,
  pickerLabel,
  disabled = false,
}) => {
  const imageInputId = useId();
  const [editingPosition, setEditingPosition] = useState<number | null>(null);
  const [mode, setMode] = useState<BannerMode>("image");
  const [draftImage, setDraftImage] = useState("");
  const [draftVideoUrl, setDraftVideoUrl] = useState("");
  const [draftRedirectUrl, setDraftRedirectUrl] = useState("");
  const [draftError, setDraftError] = useState("");
  const [pendingDeletePosition, setPendingDeletePosition] = useState<
    number | null
  >(null);
  const [confirmDraftImageDelete, setConfirmDraftImageDelete] = useState(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const sortedBanners = useMemo(
    () => [...formikVal].sort((a, b) => a.position - b.position),
    [formikVal],
  );
  const bannerSlots = Array.from({ length: 4 }, (_, index) =>
    sortedBanners.find((banner) => banner.position === index + 1),
  );

  useEffect(() => {
    if (!deleteSuccessOpen) return undefined;

    const timer = window.setTimeout(() => setDeleteSuccessOpen(false), 3000);
    return () => window.clearTimeout(timer);
  }, [deleteSuccessOpen]);

  const closeEditor = () => {
    setEditingPosition(null);
    setDraftError("");
  };

  const openEditor = (position: number) => {
    if (disabled) return;

    const banner = formikVal.find((item) => item.position === position);
    const bannerMode =
      banner?.contentType === BannerContentTypeEnum.Video ? "video" : "image";

    setEditingPosition(position);
    setMode(bannerMode);
    setDraftImage(
      banner?.contentType === BannerContentTypeEnum.Image
        ? banner.contentUrl
        : "",
    );
    setDraftVideoUrl(
      banner?.contentType === BannerContentTypeEnum.Video
        ? banner.contentUrl
        : "",
    );
    setDraftRedirectUrl(banner?.imageLink || "");
    setDraftError("");
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setDraftError("Please upload an image file.");
      return;
    }

    try {
      setDraftImage(await compressImage(file));
      setDraftError("");
    } catch {
      setDraftError(
        "We could not process that image. Please try another file.",
      );
    }
  };

  const saveDraft = () => {
    if (editingPosition === null) return;

    let nextBanner: BannerInput;

    if (mode === "image") {
      if (!draftImage) {
        setDraftError("Upload an image before saving this banner.");
        return;
      }

      nextBanner = {
        contentType: BannerContentTypeEnum.Image,
        contentUrl: draftImage,
        imageLink: draftRedirectUrl.trim() || undefined,
        position: editingPosition,
      };
    } else {
      if (!getYouTubeVideoId(draftVideoUrl.trim())) {
        setDraftError("Enter a valid YouTube video URL.");
        return;
      }

      nextBanner = {
        contentType: BannerContentTypeEnum.Video,
        contentUrl: draftVideoUrl.trim(),
        imageLink: undefined,
        position: editingPosition,
      };
    }

    formikSetFieldValue(
      [
        ...formikVal.filter((banner) => banner.position !== editingPosition),
        nextBanner,
      ].sort((a, b) => a.position - b.position),
    );
    closeEditor();
  };

  const confirmDeleteBanner = () => {
    if (pendingDeletePosition === null) return;

    formikSetFieldValue(
      formikVal.filter((banner) => banner.position !== pendingDeletePosition),
    );
    setPendingDeletePosition(null);
    setDeleteSuccessOpen(true);
  };

  const moveBanner = (position: number, direction: "left" | "right") => {
    const targetPosition = position + (direction === "left" ? -1 : 1);
    if (targetPosition < 1 || targetPosition > 4) return;

    const nextBanners = formikVal
      .map((banner) => {
        if (banner.position === position) {
          return { ...banner, position: targetPosition };
        }
        if (banner.position === targetPosition) {
          return { ...banner, position };
        }
        return banner;
      })
      .sort((a, b) => a.position - b.position);

    formikSetFieldValue(nextBanners);
  };

  const openPreview = () => {
    setPreviewIndex(0);
    setPreviewOpen(true);
  };

  const currentPreviewBanner = sortedBanners[previewIndex];
  const isImageUploaded =
    mode === "image" && Boolean(draftImage) && !draftVideoUrl;

  return (
    <div
      className={disabled ? "pointer-events-none opacity-50" : ""}
      aria-label={pickerLabel}
    >
      <div className="mt-5 grid w-full grid-cols-2 items-stretch justify-stretch gap-x-3 gap-y-4 min-[960px]:flex min-[960px]:items-center min-[960px]:justify-center min-[960px]:gap-5">
        {bannerSlots.map((banner, index) => (
          <div
            key={index}
            className="flex w-full min-w-0 flex-col items-center gap-2 min-[601px]:w-[209px] min-[601px]:gap-4"
          >
            <p className="text-center text-[15px] font-semibold leading-6 text-cpaGold min-[601px]:text-xl">
              Banner {index + 1}
            </p>

            {banner ? (
              <div className="flex h-[175px] w-full flex-col overflow-hidden rounded-[10px] border-2 border-cpaGold bg-white min-[601px]:w-[209px]">
                <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden p-3">
                  {banner.contentType === BannerContentTypeEnum.Image ? (
                    <img
                      src={banner.contentUrl}
                      alt={`Banner ${index + 1}`}
                      className="h-full w-full rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded border border-cpaGold text-cpaGold">
                      <PlayCircleOutlineIcon className="!h-9 !w-9" />
                      <span className="text-sm font-semibold">Watch Video</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between bg-transparent px-3 py-1.5">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => openEditor(banner.position)}
                      className="flex h-[33px] w-9 min-w-9 items-center justify-center rounded-[5px] bg-[#E3E3E3] p-1 text-cpaGold transition hover:bg-[#D0D0D0]"
                      aria-label={`Edit banner ${index + 1}`}
                    >
                      <EditIcon className="!h-5 !w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeletePosition(banner.position)}
                      className="flex h-[33px] w-9 min-w-9 items-center justify-center rounded-[5px] bg-[#ED1C24] p-1 text-white transition hover:bg-[#D41920]"
                      aria-label={`Delete banner ${index + 1}`}
                    >
                      <DeleteIcon className="!h-5 !w-5" />
                    </button>
                  </div>

                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => moveBanner(banner.position, "left")}
                      disabled={banner.position === 1}
                      className="flex h-[26px] w-[29px] min-w-[29px] items-center justify-center rounded-[5px] bg-cpaGold p-1 text-white transition enabled:hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:bg-[#D9D9D9] disabled:text-[#666666]"
                      aria-label={`Move banner ${index + 1} left`}
                    >
                      <ChevronLeftIcon className="!h-3 !w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBanner(banner.position, "right")}
                      disabled={banner.position === 4}
                      className="flex h-[26px] w-[29px] min-w-[29px] items-center justify-center rounded-[5px] bg-cpaGold p-1 text-white transition enabled:hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:bg-[#D9D9D9] disabled:text-[#666666]"
                      aria-label={`Move banner ${index + 1} right`}
                    >
                      <ChevronRightIcon className="!h-3 !w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => openEditor(index + 1)}
                className="flex h-[90px] w-full flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-cpaGold bg-white px-2 py-3 text-cpaGold transition hover:bg-[#FFF3D6] min-[960px]:h-[114px] min-[960px]:w-[182px] min-[960px]:gap-[10px] min-[960px]:px-3 min-[960px]:py-[22px]"
                aria-label={`Upload banner ${index + 1}`}
              >
                <FileUploadOutlinedIcon className="!h-7 !w-7 text-cpaGold min-[601px]:!h-9 min-[601px]:!w-9" />
                <span className="text-xs font-semibold leading-[1.3] min-[601px]:text-xl">
                  Upload Banner
                </span>
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="mb-[15px] mt-5 text-center text-sm font-normal leading-5 text-black sm:text-base sm:leading-6">
        After previewing your banners, click <strong>UPDATE</strong> at bottom
        of page to upload them to the Chamber Perks App™.
      </p>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={openPreview}
          disabled={sortedBanners.length === 0}
          className={`w-full rounded-xl bg-black px-6 py-[14px] text-base font-bold text-white transition hover:bg-[#222222] disabled:cursor-not-allowed disabled:bg-[#808080] min-[960px]:w-auto min-[960px]:px-24 min-[960px]:py-[15px] min-[960px]:text-xl ${
            pickerLabel === "Main Directory Banners"
              ? "mt-0"
              : "mt-4 min-[960px]:mt-[30px]"
          }`}
        >
          Preview Banners
        </button>
      </div>

      <ModalShell
        open={editingPosition !== null}
        onClose={closeEditor}
        showCloseButton={false}
        panelClassName="relative !max-w-[696px] !rounded-2xl !border-0 !p-0 min-[960px]:min-h-[681px] min-[960px]:!rounded-[20px]"
      >
        <button
          type="button"
          onClick={closeEditor}
          className="absolute right-[10px] top-[10px] z-10 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-[#ED1C24] text-white transition hover:bg-[#D41920]"
          aria-label="Close banner editor"
        >
          <CloseIcon className="!h-5 !w-5" />
        </button>

        <h3
          className={`px-12 pt-7 text-center text-[22px] font-bold leading-[1.5] min-[960px]:pt-10 min-[960px]:text-[30px] ${
            mode === "image" ? "text-cpaGold" : "text-black"
          }`}
        >
          {mode === "image" ? "Upload Image Banner" : "Upload Video Banner"}
        </h3>
        <p className="mx-auto mb-[14px] mt-[10px] max-w-[469px] px-[18px] text-center text-sm font-bold leading-[1.5] text-black min-[960px]:mb-5 min-[960px]:px-0 min-[960px]:text-lg">
          For best quality, we recommend an aspect ratio of 16:9 with a minimum
          size of 1280 × 720.
        </p>

        <div
          className={`mx-auto flex w-full flex-col px-4 min-[960px]:px-0 ${
            isImageUploaded ? "min-[960px]:max-w-[539px]" : "min-[960px]:max-w-[580px]"
          }`}
        >
          <div
            className={`relative z-[2] -mb-px flex ${
              isImageUploaded ? "" : "min-[960px]:ml-9"
            }`}
          >
            {(["image", "video"] as BannerMode[]).map((tab) => {
              const selected = mode === tab;

              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    setMode(tab);
                    setDraftError("");
                  }}
                  className={`h-10 min-w-0 flex-1 rounded-t-[10px] border border-b-0 border-dashed border-[#888888] px-4 text-[15px] font-semibold capitalize leading-6 transition min-[960px]:w-[155px] min-[960px]:flex-none min-[960px]:text-xl ${
                    selected
                      ? tab === "image"
                        ? "border-solid border-cpaGold bg-cpaGold text-white"
                        : "border-solid border-black bg-black text-white"
                      : "text-[#888888]"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          <div
            className={`relative flex w-full flex-col items-center justify-center rounded-[10px] border border-dashed border-[#888888] bg-white p-[14px] min-[960px]:p-5 ${
              isImageUploaded
                ? "min-h-[170px] min-[960px]:h-[254px]"
                : "h-[220px] min-[960px]:h-[291px]"
            }`}
          >
            <input
              id={imageInputId}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="sr-only"
            />

            {mode === "image" ? (
              draftVideoUrl ? (
                <div className="flex h-auto w-full items-center gap-[10px] rounded border-2 border-[#ED1C24] px-3 py-2 text-sm font-semibold leading-[1.5] text-[#ED1C24] min-[960px]:h-[58px] min-[960px]:w-[519px] min-[960px]:px-[10px] min-[960px]:py-0 min-[960px]:text-lg">
                  Please delete the existing YouTube video URL for this banner
                  to upload an image.
                </div>
              ) : draftImage ? (
                <img
                  src={draftImage}
                  alt="Banner preview"
                  className="max-h-[150px] w-full rounded object-contain min-[960px]:h-[213px] min-[960px]:w-[409px] min-[960px]:max-h-none min-[960px]:object-cover"
                />
              ) : (
                <label
                  htmlFor={imageInputId}
                  className="flex cursor-pointer flex-col items-center text-cpaGold"
                >
                  <FileUploadOutlinedIcon className="!h-14 !w-14 min-[960px]:!h-20 min-[960px]:!w-20" />
                  <span className="mt-[10px] text-[15px] font-semibold leading-5 underline min-[960px]:text-xl min-[960px]:leading-6">
                    Upload Image
                  </span>
                </label>
              )
            ) : draftImage ? (
              <div className="flex h-auto w-full items-center gap-[10px] rounded border-2 border-[#ED1C24] px-3 py-2 text-sm font-semibold leading-[1.5] text-[#ED1C24] min-[960px]:h-[58px] min-[960px]:w-[519px] min-[960px]:px-[10px] min-[960px]:py-0 min-[960px]:text-lg">
                Please delete the existing image for this banner to enter the
                YouTube Video URL.
              </div>
            ) : (
              <>
                {draftVideoUrl ? (
                  <PlayCircleOutlineIcon className="!h-[49px] !w-[49px] text-black" />
                ) : (
                  <VideoLibraryOutlinedIcon className="!h-20 !w-20 text-black min-[960px]:!h-[109px] min-[960px]:!w-[118px]" />
                )}
                <p className="mt-[10px] text-[15px] font-semibold leading-5 text-black min-[960px]:text-xl min-[960px]:leading-6">
                  {draftVideoUrl ? "Watch Video" : "Enter Video Link"}
                </p>
                <div
                  className={`mt-2 flex h-10 w-full max-w-[400px] items-center rounded-lg border bg-white px-3 ${
                    draftVideoUrl ? "border-cpaGold" : "border-[#D9D9D9]"
                  }`}
                >
                  <LinkIcon className="mr-2 !h-5 !w-5 -rotate-45 text-[#999999]" />
                  <input
                    type="url"
                    value={draftVideoUrl}
                    onChange={(event) => setDraftVideoUrl(event.target.value)}
                    placeholder="https://thisisayoutubevideolink.com"
                    className="min-w-0 flex-1 border-0 bg-transparent text-base text-black outline-none"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {isImageUploaded ? (
          <div className="mx-auto flex w-full max-w-[539px] flex-col items-center px-4 min-[960px]:px-0">
            <label className="mt-2 block w-full max-w-[400px] text-[13px] font-medium leading-5 text-[#333333]">
              Add a link to redirect users when they tap on image
              <span className="mt-1.5 flex h-10 items-center rounded-lg border border-[#D9D9D9] bg-white px-3">
                <LinkIcon className="mr-2 !h-5 !w-5 -rotate-45 text-[#999999]" />
                <input
                  type="url"
                  value={draftRedirectUrl}
                  onChange={(event) =>
                    setDraftRedirectUrl(event.target.value)
                  }
                  placeholder="Image Link"
                  className="min-w-0 flex-1 border-0 bg-transparent text-base text-black outline-none"
                />
              </span>
            </label>
            <div className="mt-[10px] flex w-full flex-col justify-center gap-[10px] min-[960px]:flex-row min-[960px]:gap-[15px]">
              <label
                htmlFor={imageInputId}
                className="inline-flex h-[42px] w-full cursor-pointer items-center justify-center gap-1.5 rounded-[10px] bg-cpaGold px-4 text-[15px] font-semibold uppercase leading-none text-white shadow-[0_4px_4px_rgba(0,0,0,0.25)] hover:bg-cpaGoldDark min-[960px]:w-[207px]"
              >
                <ImageOutlinedIcon className="!h-6 !w-6" /> Change Image
              </label>
              <button
                type="button"
                onClick={() => setConfirmDraftImageDelete(true)}
                className="inline-flex h-[42px] w-full items-center justify-center gap-1.5 rounded-[10px] bg-[#ED1C24] px-4 text-[15px] font-semibold uppercase leading-none text-white shadow-[0_4px_4px_rgba(0,0,0,0.25)] hover:bg-[#D41920] min-[960px]:w-[195px]"
              >
                <DeleteIcon className="!h-6 !w-6" /> Delete Image
              </button>
            </div>
          </div>
        ) : null}

        {draftError ? (
          <p className="mt-4 text-center text-sm font-semibold text-red-600">
            {draftError}
          </p>
        ) : null}

        <div className="flex flex-col items-center px-4 pb-5 pt-2 text-center min-[960px]:px-[38px]">
          <p className="mb-3 max-w-[500px] text-[13px] font-normal leading-[1.5] text-black min-[960px]:text-sm">
            To make your changes visible in the banner on Chamber Perks App™,
            click Save Banner then click Update at the bottom of the page.
          </p>
          <button
            type="button"
            onClick={saveDraft}
            className="h-[42px] w-full max-w-[280px] rounded-[10px] bg-cpaGold px-8 text-[15px] font-semibold uppercase leading-none text-white shadow-[0_4px_4px_rgba(0,0,0,0.25)] hover:bg-cpaGoldDark min-[960px]:w-[170px]"
          >
            SAVE BANNER
          </button>
        </div>
      </ModalShell>

      <ModalShell
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Banner Preview"
        panelClassName="max-w-[900px] rounded-2xl"
      >
        {currentPreviewBanner ? (
          <div>
            <div className="relative mx-auto aspect-video w-full max-w-[760px] overflow-hidden rounded-xl border-2 border-gray-200 bg-white">
              {currentPreviewBanner.contentType ===
              BannerContentTypeEnum.Video ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                    currentPreviewBanner.contentUrl,
                  )}?rel=0`}
                  title={`Banner ${previewIndex + 1} video`}
                  className="h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              ) : (
                <img
                  src={currentPreviewBanner.contentUrl}
                  alt={`Banner ${previewIndex + 1}`}
                  className="h-full w-full object-contain"
                />
              )}
              {sortedBanners.length > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setPreviewIndex(
                        (previewIndex - 1 + sortedBanners.length) %
                          sortedBanners.length,
                      )
                    }
                    className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-black shadow"
                    aria-label="Previous banner"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPreviewIndex((previewIndex + 1) % sortedBanners.length)
                    }
                    className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-black shadow"
                    aria-label="Next banner"
                  >
                    <ChevronRightIcon />
                  </button>
                </>
              ) : null}
            </div>
            <div className="mt-4 flex justify-center gap-2">
              {sortedBanners.map((banner, index) => (
                <button
                  key={banner.position}
                  type="button"
                  onClick={() => setPreviewIndex(index)}
                  className={`h-2.5 w-2.5 rounded-full ${
                    index === previewIndex ? "bg-cpaGold" : "bg-gray-300"
                  }`}
                  aria-label={`Show banner ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : null}
      </ModalShell>

      <ConfirmDialog
        open={pendingDeletePosition !== null}
        variant="destructive"
        title="Delete Banner"
        message="Are you sure you want to delete?"
        confirmLabel="DELETE"
        uppercaseActions
        hideCancel
        onConfirm={confirmDeleteBanner}
        onClose={() => setPendingDeletePosition(null)}
      />
      <ConfirmDialog
        open={confirmDraftImageDelete}
        variant="destructive"
        title="Delete Banner"
        message="Are you sure you want to delete?"
        confirmLabel="DELETE"
        uppercaseActions
        hideCancel
        onConfirm={() => {
          setDraftImage("");
          setDraftRedirectUrl("");
          setConfirmDraftImageDelete(false);
        }}
        onClose={() => setConfirmDraftImageDelete(false)}
      />
      <ConfirmDialog
        open={deleteSuccessOpen}
        variant="success"
        appearance="adminChamberSuccess"
        title="Banner Deleted Successfully!"
        onClose={() => setDeleteSuccessOpen(false)}
      />
    </div>
  );
};

export default BannerPicker;
